
import React, { useState } from 'react';
import type { BaseProblem, GeneratedProblem, ProblemParts } from '../types';
import ProblemBodyRenderer from './ProblemBodyRenderer';
import LatexRenderer from './LatexRenderer';
import toast from 'react-hot-toast';
import { useDialog } from '../contexts/DialogContext';

const getProblemMarkdown = (parts: ProblemParts): string => {
  if (!parts) return '';
  let markdown = parts.lines.join('\n');
  if (parts.type === '객관식' && parts.choices) {
    markdown += `\n\n${parts.choices}`;
  }
  return markdown;
};

const CopyButton: React.FC<{ textToCopy: string; label: string }> = ({ textToCopy, label }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!textToCopy) return;

        navigator.clipboard.writeText(textToCopy).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(err => {
            console.error('Failed to copy text: ', err);
            toast.error('클립보드 복사에 실패했습니다.');
        });
    };

    const buttonClass = `text-xs px-2 py-1 rounded-md transition-colors duration-200 ${copied ? 'bg-green-600 text-white' : 'bg-zinc-200 hover:bg-zinc-300 text-zinc-800'}`;

    return (
        <button onClick={handleCopy} className={buttonClass}>
            {copied ? '복사 완료!' : label}
        </button>
    );
};


interface TrashedProblemCardProps {
  problem: BaseProblem | GeneratedProblem;
  onRestore: (problem: BaseProblem | GeneratedProblem) => Promise<void>;
  onPermanentlyDelete: (problem: BaseProblem | GeneratedProblem) => Promise<void>;
}

const TrashedProblemCard: React.FC<TrashedProblemCardProps> = ({ problem, onRestore, onPermanentlyDelete }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const dialog = useDialog();
  
  const isGenerated = 'generatedParts' in problem;
  const parts = isGenerated ? problem.generatedParts : problem.parts;
  const croppedImage = !isGenerated ? (problem as BaseProblem).croppedImage : undefined;
  
  const handleRestore = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsProcessing(true);
    try {
      await onRestore(problem);
    } catch (e) {
      toast.error('복원에 실패했습니다.');
      setIsProcessing(false);
    }
    // No need to set isProcessing to false on success, as the component will unmount
  };
  
  const handlePermanentDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmed = await dialog.confirm("이 작업은 되돌릴 수 없습니다. 정말로 영구 삭제하시겠습니까?", { destructive: true });
    if (!confirmed) {
        return;
    }
    setIsProcessing(true);
    try {
      await onPermanentlyDelete(problem);
    } catch (e) {
      toast.error('영구 삭제에 실패했습니다.');
      setIsProcessing(false);
    }
    // No need to set isProcessing to false on success, as the component will unmount
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-lg shadow-lg flex flex-col relative transition-all duration-200">
      <div className="p-5 flex-grow">
        <div className="flex justify-between items-start mb-2">
          <div className="text-xs text-zinc-600">
            <p className="font-semibold text-black">{isGenerated ? '변형 문제' : '기반 문제'} | 삭제일: {problem.deletedAt ? new Date(problem.deletedAt).toLocaleDateString() : 'N/A'}</p>
            <p>원래 위치: <span className="font-mono">{problem.originalFolder || '/'}</span></p>
          </div>
           <CopyButton textToCopy={getProblemMarkdown(parts)} label="문제 복사" />
        </div>
        <div className="bg-white text-zinc-900 border border-zinc-200 p-4 rounded-md mb-4 sketchbook">
            {croppedImage && (
                <div className="pb-4 mb-4 border-b border-zinc-200">
                    <img src={`data:image/jpeg;base64,${croppedImage}`} alt="Cropped problem" className="w-full h-auto rounded-md" />
                </div>
            )}
           <ProblemBodyRenderer lines={parts.lines} figureImage={parts.figureImage} />
            {parts.type === '객관식' && parts.choices && (
              <div className="bg-zinc-50 p-4 rounded-lg mt-4 border border-zinc-200">
                <LatexRenderer latex={parts.choices} />
              </div>
            )}
        </div>
      </div>
      <div className="bg-zinc-50 p-3 border-t border-zinc-200 flex gap-2 rounded-b-lg">
        <button 
          onClick={handleRestore}
          disabled={isProcessing}
          className="flex-1 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 text-sm font-bold py-2 rounded-md transition-colors disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          {isProcessing ? '처리 중...' : '복원하기'}
        </button>
        <button
          onClick={handlePermanentDelete}
          disabled={isProcessing}
          className="flex-1 bg-red-100 hover:bg-red-200 text-red-700 text-sm font-bold py-2 rounded-md transition-colors disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          {isProcessing ? '처리 중...' : '영구 삭제'}
        </button>
      </div>
    </div>
  );
};

export default TrashedProblemCard;
